import { FileNode, NodeType } from "./types";
import { apiClient } from "../../utils/apiClient";
import { downloadFromResponse } from "../../utils/fileDownload";
import type {
  FolderItemResponseDto,
  FolderPermission,
} from "@secure-caseworker/shared";

/**
 * Helper to convert a folder DTO to a FileNode
 */
function folderToFileNode(folder: any, parentId: string | null): FileNode {
  return {
    id: folder.id,
    name: folder.name,
    type: "folder",
    parentId,
    createdAt:
      folder.created_at || folder.createdAt || new Date().toISOString(),
    updatedAt:
      folder.updated_at || folder.updatedAt || new Date().toISOString(),
    clientPermission: folder.clientPermission || "none",
  };
}

/**
 * Helper to convert a file DTO to a FileNode
 */
function fileToFileNode(file: any, parentId: string | null): FileNode {
  const fileName =
    file.filename || file.original_filename || file.name || "Unnamed File";
  const nameParts = fileName.split(".");
  const ext =
    nameParts.length > 1 ? nameParts[nameParts.length - 1] : undefined;

  return {
    id: file.id || file.fileId,
    name: fileName,
    type: "file",
    parentId,
    size: file.size || file.size_bytes || file.fileSize,
    ext,
    mimeType: file.mime_type || file.mimeType,
    createdAt:
      file.created_at ||
      file.createdAt ||
      file.uploadedAt ||
      new Date().toISOString(),
    updatedAt: file.updated_at || file.updatedAt || new Date().toISOString(),
  };
}

/**
 * Helper to convert a form submission DTO to a FileNode
 */
function formSubmissionToFileNode(
  submission: any,
  parentId: string | null
): FileNode {
  const submittedDate =
    submission.created_at || submission.createdAt || new Date().toISOString();
  return {
    id: submission.id,
    name: `Form Submission - ${new Date(submittedDate).toLocaleString()}`,
    type: "form_submission",
    parentId,
    createdAt: submittedDate,
    updatedAt: submission.updated_at || submission.updatedAt || submittedDate,
  };
}

/**
 * Returns nodes inside folder (pagination optional)
 * If folderId is 'root', returns root-level items for the case
 */
export async function apiListFolder(
  folderId: string,
  caseId?: string
): Promise<FileNode[]> {
  const nodes: FileNode[] = [];

  // Handle 'root' special case - get root folders and files for the case
  if (folderId === "root") {
    if (!caseId) {
      throw new Error("caseId is required when listing root items");
    }

    // Get root folders
    const rootFolders = await apiClient.getRootFolders(caseId);
    rootFolders.forEach((folder) => {
      nodes.push(folderToFileNode(folder, null));
    });

    // Get root-level files (files not in any folder)
    const files = await apiClient.listFilesByCase(caseId);
    // Filter files that don't have a parent folder
    // TODO: Backend should provide an endpoint to get only root-level files
    files.forEach((file) => {
      nodes.push(fileToFileNode(file, null));
    });

    return nodes;
  }

  // Regular folder contents
  const contents = await apiClient.getFolderContents(folderId);

  // The backend returns an array of FolderItemResponseDto with populated items
  if (Array.isArray(contents)) {
    contents.forEach((folderItem: FolderItemResponseDto) => {
      const item = folderItem.item;
      if (!item) return;

      if (folderItem.itemType === "folder") {
        nodes.push(folderToFileNode(item, folderId));
      } else if (folderItem.itemType === "file") {
        nodes.push(fileToFileNode(item, folderId));
      } else if (folderItem.itemType === "form_submission") {
        nodes.push(formSubmissionToFileNode(item, folderId));
      }
    });
  }

  return nodes;
}

/**
 * Move a node to a new parent
 */
export async function apiMove(
  node: FileNode,
  targetParentId: string | null
): Promise<FileNode> {
  try {
    // Form submissions cannot be moved
    if (node.type === "form_submission") {
      throw new Error("Form submissions cannot be moved");
    }

    if (!targetParentId) {
      // Moving to root - remove from any folder
      await apiClient.removeItemFromAllFolders(node.id, node.type);
    } else {
      // Moving to a folder - add to folder (backend handles moving between folders)
      const payload: any = {
        itemType: node.type,
      };

      if (node.type === "file") {
        payload.fileId = node.id;
      } else if (node.type === "folder") {
        payload.childFolderId = node.id;
      }

      await apiClient.addItemToFolder(targetParentId, payload);
    }

    // Return the node with updated parentId
    return {
      ...node,
      parentId: targetParentId,
      updatedAt: new Date().toISOString(),
    };
  } catch (fileError) {
    throw new Error("Failed to move item");
  }
}

/**
 * Rename node
 */
export async function apiRename(
  _nodeId: string,
  _newName: string
): Promise<FileNode> {
  // TODO: Implement rename when backend supports it
  throw new Error("Rename operation not yet supported");
}

/**
 * Delete node (soft delete/trash or hard)
 */
export async function apiDelete(
  nodeId: string,
  nodeType?: string
): Promise<{ success: boolean }> {
  console.log("node", nodeId);
  console.log("nodeType", nodeType);
  // If we don't know the type, try folder first since folders are more likely to fail gracefully
  if (nodeType === "folder" || !nodeType) {
    try {
      await apiClient.deleteFolder(nodeId);
      return { success: true };
    } catch (error: any) {
      // If folder delete fails with 404 or forbidden, it might be a file
      if (
        !nodeType &&
        (error.message.includes("not found") ||
          error.message.includes("Forbidden"))
      ) {
        await apiClient.deleteFile(nodeId);
        return { success: true };
      }
      throw error;
    }
  } else {
    await apiClient.deleteFile(nodeId);
    return { success: true };
  }
}

/**
 * Create folder under parent
 */
export async function apiCreateFolder(
  parentId: string | null,
  name: string,
  caseId?: string,
  clientPermission?: FolderPermission
): Promise<FileNode> {
  if (!caseId) {
    throw new Error("caseId is required for folder creation");
  }

  const folderData = {
    name,
    caseId,
    ...(parentId && { parentId }),
    ...(clientPermission && { clientPermission }),
  };
  console.log(folderData, "folderData");
  const response = await apiClient.createFolder(folderData);

  // Convert API response to FileNode format
  return {
    id: response.id,
    name: response.name,
    type: "folder",
    parentId: response.parentId || null,
    createdAt: response.createdAt,
    updatedAt: response.updatedAt,
    clientPermission: response.clientPermission,
  };
}

/**
 * Upload file with progress tracking
 */
export async function apiUploadFile(
  parentId: string | null,
  file: File,
  onProgress: (pct: number) => void,
  caseId?: string,
  organizationId?: string
): Promise<FileNode> {
  if (!caseId) {
    throw new Error("caseId is required for file upload");
  }
  if (!organizationId) {
    throw new Error("organizationId is required for file upload");
  }

  const formData = new FormData();
  formData.append("file", file);
  // New ownership model: files belong to a case with explicit owner type
  formData.append("ownerType", "CASE");
  formData.append("ownerId", caseId);
  formData.append("organizationId", organizationId);
  formData.append("filename", file.name);

  // Include folderId if uploading to a folder
  if (parentId) {
    formData.append("folderId", parentId);
  }

  const response = await apiClient.uploadFileWithProgress(
    "/files/upload",
    formData,
    onProgress
  );

  // Convert API response to FileNode format
  const fileNode: FileNode = {
    id: response.id || response.fileId,
    name: response.filename || file.name,
    type: "file",
    parentId: null,
    size: response.fileSize || response.size || file.size,
    mimeType: response.mimeType || file.type,
    ext: file.name.split(".").pop() || undefined,
    createdAt: response.createdAt || new Date().toISOString(),
    updatedAt: response.updatedAt || new Date().toISOString(),
  };

  return fileNode;
}

/**
 * Download file
 */
export async function apiDownloadFile(
  nodeId: string,
  filename: string
): Promise<void> {
  const response = await apiClient.downloadFileResponse(nodeId);
  await downloadFromResponse(response, filename);
}

/**
 * Update folder permission
 */
export async function apiUpdateFolderPermission(
  folderId: string,
  clientPermission: FolderPermission
): Promise<FileNode> {
  const response = await apiClient.updateFolder(folderId, {
    clientPermission,
  });

  // Convert API response to FileNode format
  return {
    id: response.id,
    name: response.name,
    type: "folder",
    parentId: response.parentId || null,
    createdAt: response.createdAt,
    updatedAt: response.updatedAt,
    clientPermission: response.clientPermission,
  };
}

/**
 * Batch operations for multi-select
 */
export async function apiBatchMove(
  nodes: FileNode[],
  targetParentId: string | null
): Promise<FileNode[]> {
  if (!targetParentId) {
    throw new Error("Cannot move without root");
  }
  return Promise.all(nodes.map((node) => apiMove(node, targetParentId)));
}

export async function apiBatchDelete(
  nodes: {
    id: string;
    type: NodeType;
  }[]
): Promise<{ success: boolean }> {
  await Promise.all(
    nodes.map((node) => {
      if (node.type === "folder") {
        return apiClient.deleteFolder(node.id);
      } else if (node.type === "form_submission") {
        // TODO: Implement form submission deletion endpoint
        throw new Error("Form submissions cannot be deleted");
      } else {
        return apiClient.deleteFile(node.id);
      }
    })
  );

  return { success: true };
}
